import React from "react";
import { Link } from "react-router-dom";
import { FiMapPin, FiClock, FiArrowRight } from "react-icons/fi";
import CareersSubNav from "../components/careersSubNav";
import ImageWithFallback from "../components/imageWithFallback";
import { openJobs, upcomingJobs } from "../data/jobs";

const Careers = () => {
  return (
    <div>
      {/* Hero */}
      <section className="bg-cloud">
        <div className="max-w-content mx-auto grid grid-cols-1 items-center gap-10 px-6 py-14 lg:grid-cols-2 lg:gap-16 lg:px-10 lg:py-20">
          <div>
            <h1 className="text-3xl font-bold leading-tight text-brand sm:text-4xl lg:text-[2.75rem]">
              Welcome to careers at Nirkana
            </h1>
            <p className="mt-4 max-w-prose text-lg text-muted">
              Join us while we're small. We're building the platform for
              how India plans its events — starting with birthdays, and
              growing from there.
            </p>
            <p className="mt-4 max-w-prose text-sm text-muted">
              We're a small, founder-led team, so the people we bring on
              now help shape how Nirkana actually works — not just fill a
              seat in an existing structure. If you'd rather build
              something from the ground up than fit into one, you'll
              probably like it here.
            </p>
            <div className="mt-7 flex flex-col gap-3 sm:flex-row">
              <a
                href="#current-openings"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-action px-6 py-3 text-base font-medium text-paper hover:bg-action/90 transition-colors"
              >
                See current openings
                <FiArrowRight />
              </a>
              <Link
                to="/careers/how-we-hire"
                className="inline-flex items-center justify-center rounded-full border border-line px-6 py-3 text-base font-medium text-brand hover:border-brand transition-colors"
              >
                How we hire
              </Link>
            </div>
          </div>

          <ImageWithFallback
            src="/assets/careers-hero.jpg"
            alt="Life at Nirkana"
            placeholderLabel="Careers hero image goes here — see setup notes"
            className="aspect-[4/5] w-full max-w-xs mx-auto rounded-2xl object-cover lg:max-w-sm"
          />
        </div>
      </section>

      <CareersSubNav />

      {/* Current openings */}
      <section id="current-openings" className="bg-paper py-10 lg:py-14 scroll-mt-16">
        <div className="max-w-content mx-auto px-6 lg:px-10">
          <h2 className="text-2xl font-bold text-brand sm:text-3xl">Current openings</h2>

          <div className="mt-8 space-y-4">
            {openJobs.map((job) => (
              <Link
                key={job.slug}
                to={`/careers/jobs/${job.slug}`}
                className="block rounded-2xl border border-line bg-cloud p-6 transition-colors hover:border-action"
              >
                <h3 className="text-lg font-semibold text-charcoal">{job.title}</h3>
                <div className="mt-3 flex flex-wrap gap-3 text-sm">
                  <span className="flex items-center gap-1.5 rounded-full bg-paper px-3 py-1 text-muted">
                    <FiMapPin size={13} /> {job.location}
                  </span>
                  <span className="flex items-center gap-1.5 rounded-full bg-paper px-3 py-1 text-muted">
                    <FiClock size={13} /> {job.experience}
                  </span>
                  <span className="rounded-full bg-paper px-3 py-1 text-muted">{job.department}</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Future openings */}
      <section className="bg-cloud py-10 lg:py-14">
        <div className="max-w-content mx-auto px-6 lg:px-10">
          <h2 className="text-2xl font-bold text-brand sm:text-3xl">Future openings</h2>
          <p className="mt-3 max-w-prose text-muted">
            Not accepting applications yet, but here's what's coming as we
            build out the team beyond launch.
          </p>

          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {upcomingJobs.map((job) => (
              <div key={job.title} className="rounded-2xl border border-line bg-paper p-5">
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-medium text-charcoal">{job.title}</h3>
                  <span className="shrink-0 rounded-full bg-gold-soft px-2.5 py-1 text-[11px] font-semibold text-gold">
                    Upcoming
                  </span>
                </div>
                <p className="mt-2 text-sm text-muted">
                  {job.experience} · {job.department}
                  {job.openings ? ` · ${job.openings} openings` : ""}
                </p>
              </div>
            ))}
          </div>

          <p className="mt-8 text-sm text-muted">
            Don't see your role listed yet, or want a head start? Send your
            profile to{" "}
            <a href="mailto:info@nirkana.com" className="font-medium text-action hover:underline">
              info@nirkana.com
            </a>{" "}
            and we'll reach out when it opens.
          </p>
        </div>
      </section>
    </div>
  );
};

export default Careers;
